import alert_mixin from '@/utils/mixins/alert_mixin';
import { get_headers , USER_VERIFICATION_RESULTS} from '@/utils/constants/constants.js';
import axios from 'axios';
import { getAMLVerificationResults } from "@/services/verification/integrated_verification_apis/aml_verification";
import AMLDashboardResults from '@/components/probaworkflows_base_components/AutoVerificationResults/AMLDashboardResults.vue';

export default {
  name: "VerificationResults",
  mixins: [alert_mixin],
   components: {
    AMLDashboardResults
  },
  props: {
    entity_profile_id: {
      type: [String, Number],
      required: false,
      default: null
    },
    lead_id: {
      type: [String, Number],
      required: false,
      default: null
    }
  },
  data: () => ({
    headers: get_headers(),
    expanded_index: null,
    active_tabs: ['Forms'],
    is_trust_form_open: true,
    verification_results: [],
    loading_results: false,
    error_message: '',
  }),
  
  computed: {
    is_lead_context() {
      return !!this.resolved_lead_id;
    },
    
    resolved_entity_profile_id() {
      if (this.entity_profile_id) {
        return this.entity_profile_id;
      }
      
      const route_entity_id = this.$route?.query?.entity_profile_id;
      if (route_entity_id) {
        return route_entity_id;
      }
      
      const storage_entity_id = sessionStorage.getItem('entity_profile_id');
      if (storage_entity_id) {
        return storage_entity_id;
      }
      
      return null;
    },
    
    resolved_lead_id() {
      if (this.lead_id) {
        return this.lead_id;
      }
      
      const route_lead_id = this.$route?.query?.lead_id;
      if (route_lead_id) {
        return route_lead_id;
      }
      
      const storage_lead_id = sessionStorage.getItem('lead_id');
      if (storage_lead_id) {
        return storage_lead_id;
      }
      
      return null;
    },
    
    query_params() {
      let params;

      if (this.resolved_entity_profile_id) {
        params = { entity_profile_id: this.resolved_entity_profile_id };
      } else {
        params = { lead_id: this.resolved_lead_id };
      }

      return params;
    }

  },

  watch: {
    resolved_entity_profile_id(newVal) {
      if (newVal || this.resolved_lead_id) {
        this.fetch_verification_results();
      }
    },
    resolved_lead_id(newVal) {
      if (newVal || this.resolved_entity_profile_id) {
        this.fetch_verification_results();
      }
    }
  },
  
  created() {
    if (this.resolved_entity_profile_id || this.resolved_lead_id) {
      this.fetch_verification_results();
    }
  },
  
  methods: {
    async fetch_verification_results() {
      if (!this.query_params) {
        return;
      }

      this.loading_results = true;
      this.error_message = '';

      try {
        const response = await axios.get(USER_VERIFICATION_RESULTS, {
          headers: this.headers,
          params: this.query_params
        });
        
        let results = response.data?.data || response.data || [];

        if (Array.isArray(results)) {
          results = results.filter(service => service && (service.id || service.name));

          // Enrich AML results - pass the appropriate context
          const aml_promises = results.map(service => {
            if (!service.name || !service.name.toLowerCase().includes('aml')) return null;

            const aml_params = {
              service_id: service.id
            };
            
            // Add the appropriate ID based on context
            if (this.resolved_entity_profile_id) {
              aml_params.entity_profile_id = this.resolved_entity_profile_id;
            } else if (this.resolved_lead_id) {
              aml_params.lead_id = this.resolved_lead_id;
            }

            return getAMLVerificationResults(aml_params)
              .then(aml_enriched => {
                const aml_result = aml_enriched?.results?.[0];
                if (aml_result) {
                  service.aml_check_results = aml_result.aml_check_results;
                  service.risk_score = aml_result.risk_score;
                  service.aml_results = aml_result;
                }
              })
              .catch(error => {
                this.show_error("Failed to fetch AML results", error);
              });
          }).filter(Boolean);
          
          await Promise.all(aml_promises);
          this.verification_results = results;
        } else {
          this.verification_results = [];
        }

        if (!this.verification_results.length) {
          this.error_message = 'No verification results found for this ' + 
            (this.is_lead_context ? 'lead' : 'entity') + '.';
        }
      } catch (error) {
        this.error_message = `Failed to load verification results: ${error.response?.data?.message || error.message || 'Please try again.'}`;
        this.verification_results = [];
      } finally {
        this.loading_results = false;
      }
    },

    should_show_service(service) {
      if (service.name?.toLowerCase().includes('cipc')) {
        const has_cipc_results = service.cipc_results &&
          (Array.isArray(service.cipc_results)
            ? service.cipc_results.length > 0
            : Object.keys(service.cipc_results).length > 0);
        if (!has_cipc_results) {
          return false;
        }
      }

      const result_types = ['aml_results', 'idv_results', 'credit_results', 'cipc_results', 'kyc_results'];

      for (const type of result_types) {
        const value = Reflect.get(service, type);
        if (value) {
          const has_results = Array.isArray(value)
            ? value.length > 0
            : value && Object.keys(value).length > 0;

          if (has_results) {
            return true;
          }
        }
      }

      return false;
    },

    get_overall_status(service) {
      const result_types = ['aml_results', 'idv_results', 'credit_results', 'cipc_results', 'kyc_results'];

      for (const type of result_types) {
        const value = Reflect.get(service, type);

        if (value && !Array.isArray(value) && value.status) {
          return value.status.charAt(0).toUpperCase() + value.status.slice(1);
        }

        if (Array.isArray(value) && value.length > 0 && value[0].status) {
          return value[0].status.charAt(0).toUpperCase() + value[0].status.slice(1);
        }
      }

      return 'Unknown';
    },

    get_overall_status_class(service) {
      const status = this.get_overall_status(service).toLowerCase();

      const status_map = {
        'pass': 'success',
        'completed': 'success',
        'verified': 'success',
        'success': 'success',
        'fail': 'failed',
        'failed': 'failed',
        'error': 'error',
        'pending': 'pending',
        'processing': 'processing',
        'running': 'running',
        'in progress': 'processing'
      };

      const mapped = Reflect.get(status_map, status) || 'default';
      return `status-badge ${mapped}`;
    },

    get_alert_class(service) {
      const status = this.get_overall_status(service).toLowerCase();
      
      if (status === 'pass' || status === 'success') {
        return 'alert-success';
      } else if (status === 'fail' || status === 'failed') {
        return 'alert-danger';
      } else if (status === 'pending' || status === 'processing') {
        return 'alert-warning';
      } else {
        return 'alert-info';
      }
    },

    get_service_message(service) {
      const result_types = ['aml_results', 'idv_results', 'credit_results', 'cipc_results', 'kyc_results'];
      for (const type of result_types) {
        const value = Reflect.get(service, type);

        if (value && !Array.isArray(value) && Reflect.get(value, 'message')) {
          return Reflect.get(value, 'message');
        }
      }

      return 'No status message available';
    },

    get_last_updated() {
      return new Date().toLocaleDateString();
    },

    has_field_results(service) {
      const result_types = ['aml_results', 'idv_results', 'credit_results', 'cipc_results', 'kyc_results'];

      for (const type of result_types) {
        const value = Reflect.get(service, type);
        const fieldResults = value && Reflect.get(value, 'field_results');

        if (fieldResults && Object.keys(fieldResults).length > 0) {
          return true;
        }
      }

      return false;
    },

    get_field_results(service) {
      const result_types = ['aml_results', 'idv_results', 'credit_results', 'cipc_results', 'kyc_results'];
      for (const type of result_types) {
        const fieldResults = Reflect.get(Reflect.get(service, type) || {}, 'field_results');
        if (fieldResults) return fieldResults;
      }
    },

    has_detailed_results(service) {
      const result_types = ['aml_results', 'idv_results', 'credit_results', 'cipc_results', 'kyc_results'];

      for (const type of result_types) {
        const value = Reflect.get(service, type);
        const resultsData = value && Reflect.get(value, 'results_data');

        if (resultsData && Object.keys(resultsData).length > 0) {
          return true;
        }
      }

      return false;
    },

    get_detailed_results(service) {
      const result_types = ['aml_results', 'idv_results', 'credit_results', 'cipc_results', 'kyc_results'];

      for (const type of result_types) {
        const value = Reflect.get(service, type);
        const resultsData = value && Reflect.get(value, 'results_data');
        if (resultsData) {
          return this.parse_results_data(resultsData);
        }
      }
      return null;
    },


    parse_results_data(data) {
      if (!data || typeof data !== 'object') {
        return null;
      }

      const parsed = {};

      for (const entry of Object.entries(data)) {
        const key = Reflect.get(entry, 0);
        const value = Reflect.get(entry, 1);

        if (this.isNestedObject(value)) {
          const nested = this.flatten_nested_data(value, key);
          Object.assign(parsed, nested);
        } else {
          Reflect.set(parsed, key, value);
        }
      }

      return parsed;
    },


    flatten_nested_data(obj, parentKey = '') {
      const flattened = {};

      for (const entry of Object.entries(obj)) {
        const key = Reflect.get(entry, 0);
        const value = Reflect.get(entry, 1);

        const newKey = parentKey ? `${parentKey}_${key}` : key;

        if (value === null || value === undefined || value === '') {
          continue;
        }

        if (Array.isArray(value)) {
          if (value.length > 0) {
            Reflect.set(flattened, newKey, value);
          }
          continue;
        }

        if (typeof value === 'object') {
          const value_entries = Object.values(value);
          const has_only_primitives = value_entries.every(v =>
            typeof v !== 'object' || v === null
          );

          if (has_only_primitives) {
            Reflect.set(flattened, newKey, value);
          } else {
            const nested = this.flatten_nested_data(value, newKey);
            Object.assign(flattened, nested);
          }
        } else {
          Reflect.set(flattened, newKey, value);
        }
      }

      return flattened;
    },


    get_service_icon(service_name) {
      const icon_map = {
        'AML Verification': 'bi-shield-check',
        'Credit Report Verification': 'bi-graph-up',
        'KYC Verification': 'bi-person-check',
        'IDV Verification': 'bi-person-badge',
        'CIPC Verification': 'bi-building'
      };

      const icon = Reflect.get(icon_map, service_name) || 'bi-file-earmark-text fs-4';
      return icon;
    },

    toggle_tab(tab) {
      if (this.active_tabs.includes(tab)) {
        this.active_tabs = this.active_tabs.filter(t => t !== tab);
      } else {
        this.active_tabs.push(tab);
      }
    },
    
    toggle_program() {
      this.is_trust_form_open = !this.is_trust_form_open;
    },
    
    toggle_expanded(index) {
      this.expanded_index = this.expanded_index === index ? null : index;
    },
    
    formatKey(str) {
      if (!str) return '';
      return str.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },
    
    formatValue(raw_value) {
      const format_handlers = {
        null: () => "N/A",
        undefined: () => "N/A",

        boolean: (bool_value) => (bool_value ? "Yes" : "No"),

        array: (array_value) => {
          if (array_value.length === 0) return "No entries";

          return array_value.map((element, index) => {const item_number = index + 1;

            return typeof element === "object" ? this.format_object_for_display(element, item_number): element;}).join("\n\n");
        },

        object: (object_value) => {
          return this.format_object_for_display(object_value);
        },

        default: (generic_value) => String(generic_value),
      };

      const detected_type =
        raw_value === null? "null"
          : Array.isArray(raw_value) ? "array" : typeof raw_value;

      const selected_formatter =
        Reflect.get(format_handlers, detected_type) || format_handlers.default

      return selected_formatter(raw_value);
    },

    format_object_for_display(obj, index = null) {
      if (!obj || typeof obj !== 'object') return 'N/A';

      const entries = Object.entries(obj)
        .filter(entry => {
          const value = Reflect.get(entry, 1);
          return value !== null && value !== undefined && value !== '';
        })
        .map(entry => {
          const key = Reflect.get(entry, 0);
          const value = Reflect.get(entry, 1);
          return `${this.formatKey(key)}: ${value}`;
        })
        .join(', ');

      return index ? `Entry ${index}: ${entries}` : entries;
    },

    
    isNestedObject(obj) {
      return typeof obj === 'object' && obj !== null && !Array.isArray(obj) && Object.keys(obj).length > 0;
    },

    is_complex_value(value) {
      if (Array.isArray(value) && value.length > 0) return true;
      if (typeof value === 'object' && value !== null && Object.keys(value).length > 3) return true;
      return false;
    },

    is_aml_service(service) {
      const name = (service.name || "").toLowerCase();
      return name.includes("aml") || name.includes("anti-money");
    },

    get_risk_alert_class(risk_score) {
      if (!risk_score) return "alert-secondary";
      const risk = risk_score.toLowerCase();
      if (risk.includes("high")) return "alert-danger";
      if (risk.includes("medium")) return "alert-warning";
      return "alert-success";
    },

    parse_references(comment) {
      if (!comment) return [];

      return comment
        .split("|")
        .filter((ref) => ref.trim().length > 0)
        .map((ref) => {
          const trimmed_ref = ref.trim();
          const url_pattern =
            /^https?:\/\/[^\s]+\.[^\s]+/i;
          const is_url = url_pattern.test(trimmed_ref);

          const [text, link] = is_url
            ? [
                trimmed_ref
                  .replace(/https?:\/\/(www\.)?/i, "")
                  .replace(/\/$/, ""),
                trimmed_ref,
              ]
            : [trimmed_ref, null];

          return {
            is_url,
            raw: trimmed_ref,
            display: is_url ? link : text,
          };
        });
    }
  }
};